import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Star, MapPin, Award, MessageSquare } from "lucide-react";
import { profileAvatar } from "../lib/avatar";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const ROLE_LABEL = {
    broker: "Verified Broker",
    advocate: "Property Advocate",
    loan_agent: "Loan Specialist",
};

export default function AgentCard({ agent }) {
    const img = profileAvatar(agent, 256);
    const nav = useNavigate();
    const { user } = useAuth();
    const startChat = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!user) { nav("/login?next=/chat"); return; }
        if (user.id === agent.id) return;
        try {
            const { data } = await api.post("/chat/conversations", { participant_id: agent.id });
            nav(`/chat/${data.id}`);
        } catch (err) {
            if (err?.response?.status === 402) {
                toast.error("Chat is available for subscribers. Redirecting to plans…");
                setTimeout(() => nav("/pricing"), 900);
            } else {
                toast.error(err?.response?.data?.detail || "Could not start chat");
            }
        }
    };
    return (
        <Link
            to={`/partners/${agent.id}`}
            data-testid={`agent-card-${agent.id}`}
            className="relative block rounded-2xl bg-navy-800 border border-white/5 hover:border-gold-500/40 p-5 hover-lift transition-colors"
        >
            {user?.id !== agent.id && (
                <button
                    type="button"
                    onClick={startChat}
                    data-testid={`agent-card-chat-${agent.id}`}
                    aria-label={`Chat with ${agent.name}`}
                    className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gold-500/15 border border-gold-500/40 text-gold-400 text-[11px] font-bold hover:bg-gold-500 hover:text-navy-900 transition-colors"
                >
                    <MessageSquare className="w-3.5 h-3.5" /> Chat
                </button>
            )}
            <div className="flex items-start gap-4">
                <div className="relative shrink-0">
                    <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-gold-500/30">
                        <img src={img} alt={agent.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    {agent.verified && (
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gold-500 flex items-center justify-center ring-2 ring-navy-800">
                            <ShieldCheck className="w-3.5 h-3.5 text-navy-900" strokeWidth={3} />
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-gold-500 mb-1">
                        {ROLE_LABEL[agent.role] || agent.role}
                    </div>
                    <div className="font-heading font-bold text-white text-lg leading-tight truncate">{agent.name}</div>
                    <div className="text-xs text-white/60 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {agent.city || "India"}
                    </div>
                </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-3 gap-2 text-center">
                <div>
                    <div className="text-gold-500 font-bold text-sm flex items-center justify-center gap-1">
                        <Star className="w-3.5 h-3.5 fill-gold-500" /> {(agent.rating || 0).toFixed(1)}
                    </div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wider">Rating</div>
                </div>
                <div>
                    <div className="text-white font-bold text-sm">{agent.experience_years || 0}+</div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wider">Years</div>
                </div>
                <div>
                    <div className="text-white font-bold text-sm">{agent.reviews_count || 0}</div>
                    <div className="text-[10px] text-white/50 uppercase tracking-wider">Reviews</div>
                </div>
            </div>
            {(agent.rera_number || agent.bar_council_id) && (
                <div className="mt-3 px-3 py-2 rounded-md bg-gold-500/5 border border-gold-500/20 text-[11px] text-gold-500/90 flex items-center gap-1.5">
                    <Award className="w-3 h-3" />
                    {agent.rera_number ? `RERA: ${agent.rera_number}` : `Bar ID: ${agent.bar_council_id}`}
                </div>
            )}
        </Link>
    );
}
